import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { DatePicker, Select, Spin } from 'antd';
import Alert from '../../components/Alert';
import StepFooter from '../../components/StepFooter';
import { Icon } from 'Components';
import { CAMPAIGN_LENGTH_LIST } from '../../PhishingManager.constants';
import { getPhisingTimeZones, submitStep4 } from '../../service/index.Service';
import { errorMessageHandler } from '../../utils';

type DeliveryScheduleProps = {
	tenantId: number | string | undefined;
	campaignId: string | number | null;
	isActive: boolean;
	onBack: () => void;
	onNext: () => void;
	setToast: (
		toast: { message: string; type: 'warning' | 'success' }
	) => void;
};

type DeleiveryScheduleState = {
	start_date: moment.Moment | null;
	start_date_err: string | null;
	start_time: string | number;
	start_time_err: string | null;
	campaign_length: number;
	timezone_id: string | number;
};

type Timezone = {
	id: number;
	name: string;
	offset: string;
	region: string;
};

const Step4DeliverySchedule: React.FC<DeliveryScheduleProps> = ({
	onBack,
	onNext,
	isActive,
	tenantId,
	campaignId,
	setToast
}) => {
	const [state, setState] = useState<DeleiveryScheduleState>({
		start_date: null,
		start_date_err: null,
		start_time: '',
		start_time_err: null,
		campaign_length: 7,
		timezone_id: 18
	});
	const [timezoneList, setTimezoneList] = useState<Timezone[]>([]);
	const [loading, setLoading] = useState(false);

	const disabledDate = (current: any) =>
		current &&
		(current < moment().startOf('day') ||
			current > moment().add(90, 'days'));

	const disableNextBtn: boolean = useMemo(() => {
		const {
			start_date,
			start_time,
			start_date_err,
			start_time_err
		} = state;
		return !!(
			(!start_date || !start_time || start_date_err || start_time_err) &&
			!loading
		);
	}, [state]);

	const handleSubmit = async () => {
		const { start_date, start_time, timezone_id, campaign_length } = state;

		if (!campaignId || !tenantId || disableNextBtn) return;
		const errors: Record<string, string> = {};
		if (!start_date) errors['start_date_err'] = 'Enter a valid date';
		if (!start_time) errors['start_time_err'] = 'Enter a valid time';

		if (Object.keys(errors).length > 0) {
			setState((prev) => ({ ...prev, ...errors }));
			return;
		}

		const payload = {
			start_date: moment(start_date).format('YYYY-MM-DD'),
			start_time,
			campaign_length,
			timezone_id,
			campaign_id: campaignId
		};
		try {
			setLoading(true);
			const res = await submitStep4(payload, tenantId);
			if (res?.status === 'success') onNext();
		} catch (err: any) {
			const errorMessage = errorMessageHandler(
				err,
				'Failed to create campaign step 4'
			);
			setToast({ message: errorMessage, type: 'warning' });
		} finally {
			setLoading(false);
		}
	};

	const dropdownIcon = (
		<div className="cs-dropdownWrapper">
			<Icon type="campaignSetupDropdown" />
		</div>
	);

	const TIME_OPTIONS: { value: string; label: string }[] = useMemo(() => {
		const options: { value: string; label: string }[] = [];

		for (let minutes = 7 * 60; minutes <= 17 * 60; minutes += 30) {
			const hour = Math.floor(minutes / 60);
			const minute = minutes % 60;

			const period = hour >= 12 ? 'PM' : 'AM';
			const displayHour = hour % 12 || 12;

			options.push({
				label: `${displayHour}:${minute
					.toString()
					.padStart(2, '0')} ${period}`,
				value: `${hour
					.toString()
					.padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
			});
		}

		return options;
	}, []);

	const getTimezones = useCallback(async () => {
		try {
			if (!tenantId) return;
			setLoading(true);
			const res = await getPhisingTimeZones(tenantId);
			setTimezoneList(res?.data || []);
		} catch (error) {
			setTimezoneList([]);
		} finally {
			setLoading(false);
		}
	}, [])

	useEffect(() => {
		if (isActive && !timezoneList.length) getTimezones();
	}, [isActive]);

	return (
		<>
			<Spin spinning={loading}>
				<div className="delivery-schedule-config step-card">

					<div className="card-header">
						<div className="card-title">Delivery & Timing</div>
						<div className="card-subtitle">
							Set when the emails will be sent and how long the
							campaign will run
						</div>
					</div>

					<div className="form-container">
						<span className="form-header">Schedule</span>
						<div className="form-section">
							<div className="form-item-row">
								<FormComponent
									label="Start date"
									error={state.start_date_err}
								>
									<DatePicker
										allowClear={false}
										value={state.start_date}
										format="M/D/YYYY"
										className="date-time-picker"
										disabledDate={disabledDate}
										onChange={(value) =>
											setState((prev) => ({
												...prev,
												start_date: value,
												start_date_err: null
											}))
										}
									/>
								</FormComponent>

								<FormComponent
									label="Start time"
									error={state.start_time_err}
								>
									<Select
										placeholder="Select time"
										className="campaign-setup-select"
										dropdownClassName="campaign-setup-filter-option-select"
										suffixIcon={dropdownIcon}
										value={state.start_time || undefined}
										onChange={(value) =>
											setState((prev: any) => ({
												...prev,
												start_time: value,
												start_time_err: null
											}))
										}
										options={TIME_OPTIONS}
									/>
								</FormComponent>

								<FormComponent label="Length of the campaign">
									<Select
										className="campaign-setup-select"
										dropdownClassName="campaign-setup-filter-option-select"
										suffixIcon={dropdownIcon}
										value={state.campaign_length}
										onChange={(value) =>
											setState((prev: any) => ({
												...prev,
												campaign_length: value
											}))
										}
										options={CAMPAIGN_LENGTH_LIST}
										getPopupContainer={(trigger) =>
											trigger.parentElement
										}
									/>
								</FormComponent>

								<FormComponent label="Time zone">
									<Select
										loading={loading}
										placeholder="Select your timezone"
										disabled={!timezoneList.length || loading}
										value={state.timezone_id || undefined}
										onChange={(value) =>
											setState((prev: any) => ({
												...prev,
												timezone_id: value
											}))
										}
										suffixIcon={dropdownIcon}
										className="campaign-setup-select"
										dropdownClassName="campaign-setup-filter-option-select"
										getPopupContainer={(trigger) =>
											trigger.parentElement
										}
										optionFilterProp="children"
									>
										{timezoneList.map((tz) => (
											<Select.Option
												key={tz.id}
												value={tz.id}
											>
												{`(${tz.offset}) ${tz.name}`}
											</Select.Option>
										))}
									</Select>
								</FormComponent>
							</div>
						</div>
					</div>

					<div className="alert-section">
						<Alert
							title="Delivery notice"
							subtitle="Emails will be sent incrementally within the selected time window to reduce delivery issues and system load"
						/>
					</div>
				</div>
			</Spin>
			<StepFooter
				onBack={onBack}
				onNext={handleSubmit}
				isLoading={loading}
				nextBtnDisabled={disableNextBtn}
			/>
		</>
	);
};

export default Step4DeliverySchedule;

const FormComponent = ({
	children,
	label,
	error = null
}: {
	children: React.ReactNode;
	label: string;
	error?: string | null;
}) => {
	return (
		<div className="form-item">
			<label className="form-label">
				{label} <span className="required-asterisk">*</span>
			</label>
			{children}
			{error && <div className="ml-input-help-text">{error}</div>}
		</div>
	);
};
